import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import ManagementModal from "../../../components/ui/modals/ManagementModal";
import ManagementFormField from "./ManagementFormField";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import {
  EntityType,
  RelatedAs,
} from "../../../api/stakeholder_relationships";
import {
  BorrowerConstitution,
  KycType,
} from "../../../api/enrichedStakeholder";
import { createStakeholders } from "../logic/stakeholderThunks";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import { getAccountProductInfoByAccountNumber } from "../../creditAccounts/logic/slice";
import { validateIdentifier } from "../../../components/ui/formelement/v2/EditableField";

interface AddNewBorrowerModalProps {
  accountNumber: string;
  relatedAs: string;
  onClose: () => void;
}

interface FormState {
  name: string;
  constitution: BorrowerConstitution | undefined;
  cifNumber: string;
  pan: string;
}

const AddNewBorrowerModal: React.FC<AddNewBorrowerModalProps> = ({
  accountNumber,
  relatedAs,
  onClose,
}) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const displayAccountNumber = useAppSelector(
    (state) =>
      getAccountProductInfoByAccountNumber(state, accountNumber)
        ?.ext_account_number || "--",
  );

  const [form, setForm] = useState<FormState>({
    name: "",
    constitution: undefined,
    cifNumber: "",
    pan: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isPanValid = validateIdentifier("pan", form.pan) === null;

  const isFormValid = form.name.trim().length > 0 && Boolean(form.constitution);

  const handleSubmit = async () => {
    setIsSubmitted(true);

    if (!caseId || !isFormValid || isLoading) {
      return;
    }

    if (!isPanValid) {
      return;
    }

    try {
      setIsLoading(true);

      await dispatch(
        createStakeholders({
          caseId,
          request: {
            stakeholders: [
              {
                stakeholder: {
                  name: form.name.trim(),
                },
                relationships: [
                  {
                    entity_type: EntityType.LOAN_ACCOUNT,
                    entity_id: accountNumber,
                    related_as: relatedAs as RelatedAs,
                  },
                ],
                customer_details: {
                  constitution: form.constitution,
                  cif_number: form.cifNumber.trim() || undefined,
                },
                ...(form.pan.trim()
                  ? {
                      kyc_details: [
                        {
                          kyc_id: uuidv4(),
                          kyc_type: KycType.PAN,
                          kyc_value: form.pan.trim().toUpperCase(),
                        },
                      ],
                    }
                  : {}),
              },
            ],
          },
        }),
      ).unwrap();
      showSuccessToast("Borrower added successfully.");
      onClose();
    } catch {
      showErrorToast("Failed to add borrower. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    onClose();
  };

  const constitutionOptions = [
    { name: "Individual", value: BorrowerConstitution.INDIVIDUAL },
    { name: "Sole Proprietorship", value: BorrowerConstitution.SOLE_PROPRIETORSHIP },
    { name: "One Person Company", value: BorrowerConstitution.ONE_PERSON_COMPANY },
    { name: "Partnership", value: BorrowerConstitution.PARTNERSHIP },
    { name: "Limited Liability Partnership", value: BorrowerConstitution.LIMITED_LIABILITY_PARTNERSHIP },
    { name: "Private Limited Company", value: BorrowerConstitution.PRIVATE_LIMITED_COMPANY },
    { name: "Public Limited Company", value: BorrowerConstitution.PUBLIC_LIMITED_COMPANY },
    { name: "Trust", value: BorrowerConstitution.TRUST },
    { name: "Society", value: BorrowerConstitution.SOCIETY },
    { name: "Cooperative Society", value: BorrowerConstitution.COOPERATIVE_SOCIETY },
    { name: "Association of Persons", value: BorrowerConstitution.ASSOCIATION_OF_PERSONS },
  ];

  return (
    <ManagementModal
      title="Add Borrower"
      subtitle={displayAccountNumber}
      subtitleClassName="text-[12px] leading-4 text-[#737373]"
      onSubmit={handleSubmit}
      onClose={handleClose}
      submitLabel="Add Borrower"
      submitEnabled={isFormValid}
      isLoading={isLoading}
      className="w-[608px]"
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-4">
          <ManagementFormField
            label="Full Name / Firm Name"
            required
            value={form.name}
            onChange={(value) => handleChange("name", value)}
            disabled={isLoading}
            showError={isSubmitted}
          />

          <ManagementFormField
            label="Constitution"
            type="select"
            required
            value={form.constitution}
            onChange={(value) =>
              handleChange("constitution", value as BorrowerConstitution)
            }
            options={constitutionOptions}
            disabled={isLoading}
            showError={isSubmitted}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ManagementFormField
            label="CIF Number"
            value={form.cifNumber}
            onChange={(value) => handleChange("cifNumber", value)}
            disabled={isLoading}
          />

          <ManagementFormField
            label="PAN"
            type="pan"
            value={form.pan}
            onChange={(value) => handleChange("pan", value)}
            disabled={isLoading}
            showError={isSubmitted}
          />
        </div>
      </div>
    </ManagementModal>
  );
};

export default AddNewBorrowerModal;